import {
  FurtherInvestigation,
  FurtherInvestigationFeedback,
  Police,
  Prosecutor,
  ProsecutorDecisionFeedback,
  InvestigationFile,
  Schedule,
  PreliminaryInquiry,
  CoordinatorComment,
  CoordinatorCommentOnInvestigationFile,
} from "types";
import apiClient from "../lib/apiClients";

// Backend list endpoints may return a plain array (legacy) or a paginated
// { next, previous, results } envelope. Normalize both to an array.
const unwrapList = <T>(data: any): T[] =>
  Array.isArray(data) ? data : (data?.results ?? []);

// Page-number pagination envelope ({ count, next, previous, results }).
export interface PageResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  prosecutor_decision?: string; // high-level record-based decision filter
  prosecutor_decision_type?: string;
  prosecutor_decision_status?: string;
  is_re_intiated?: boolean;
  is_case_suspended?: boolean;
}

// Builds the query string: endpoint-specific params (extra) + pagination params.
const buildListQuery = (
  params?: ListParams,
  extra?: Record<string, string | number>,
) => {
  const q = new URLSearchParams();
  if (extra)
    Object.entries(extra).forEach(([k, v]) => q.set(k, String(v ?? "")));
  if (params?.page) q.set("page", String(params.page));
  if (params?.pageSize) q.set("page_size", String(params.pageSize));
  if (params?.search) q.set("search", params.search);
  if (params?.status) q.set("status", params.status);
  if (params?.prosecutor_decision)
    q.set("prosecutor_decision", params.prosecutor_decision);
  if (params?.prosecutor_decision_type)
    q.set("prosecutor_decision_type", params.prosecutor_decision_type);
  if (params?.prosecutor_decision_status)
    q.set("prosecutor_decision_status", params.prosecutor_decision_status);
  if (params?.is_re_intiated) q.set("is_re_intiated", "true");
  if (params?.is_case_suspended) q.set("is_case_suspended", "true");
  return q.toString();
};

// Normalizes either a plain array or a { count, ... } envelope into PageResult.
const toPage = <T>(data: any): PageResult<T> =>
  Array.isArray(data)
    ? { count: data.length, next: null, previous: null, results: data }
    : {
        count: data?.count ?? data?.results?.length ?? 0,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        results: data?.results ?? [],
      };

export const fetchProsecutor = async () => {
  const response = await apiClient.get<Prosecutor[]>(
    "/prosecutor-police/prosecutors/",
  );
  return response.data;
};

export const fetchPolice = async () => {
  const response = await apiClient.get<Police[]>("/prosecutor-police/police/");
  return response.data;
};

// Schedule
export const fetchSchedule = async () => {
  const response = await apiClient.get<Schedule[]>(
    "/prosecutor-police/schedules/",
  );
  return response.data;
};
export const fetchScheduleForPolice = async (police_station_id: string) => {
  const response = await apiClient.get<Schedule[]>(
    `/prosecutor-police/schedules/by-police-station/?police_station_id=${police_station_id}`,
  );
  return response.data;
};
export const fetchScheduleForCoordinator = async (admin_unit: string) => {
  const response = await apiClient.get<Schedule[]>(
    `/prosecutor-police/schedules/by-admin-unit/?adminunit_id=${admin_unit}`,
  );
  return response.data;
};
export const addSchedule = async (data: Schedule) => {
  const response = await apiClient.post("/prosecutor-police/schedules/", data);
  return response.data;
};
export const editSchedule = async (data: Schedule) => {
  const response = await apiClient.put(
    "/prosecutor-police/schedules/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteSchedule = async (reporter: Schedule) => {
  const response = await apiClient.delete(
    "/prosecutor-police/schedules/" + reporter?.id + "/",
  );
  return response.data;
};

// Receiving Investigation File List
export const fetchReceivingInvestigationFile = async () => {
  const response = await apiClient.get(
    "/prosecutor-police/investigation-files/",
  );
  return unwrapList<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileForClerk = async (role: string) => {
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-clerk-role/?clerk_role=${role}`,
  );
  return unwrapList<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByCoordinator = async (
  admin_unit: string | number,
  org_id: string | number,
) => {
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-admin-org/?admin_unit_id=${admin_unit}&org_id=${org_id}`,
  );
  return unwrapList<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByCoordinatorWithType = async (
  admin_unit: string | number,
  org_id: string | number,
  role: string,
) => {
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-admin-org-coordinator/?admin_unit_id=${admin_unit}&coordinator_role=${role}`,
  );
  return unwrapList<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByProsecutor = async (
  assign_prosecutor_id: string | number,
) => {
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-prosecutor-id/?assign_prosecutor_id=${assign_prosecutor_id}`,
  );
  return unwrapList<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByPolice = async (
  police_station_id: string | number,
) => {
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-police-station/?police_station_id=${police_station_id}`,
  );
  return unwrapList<InvestigationFile>(response.data);
};

// --- Page-number paginated variants (return { count, next, previous, results }) ---
// Each accepts ListParams { page, pageSize, search, status }. Falls back to a
// single-page result if a given endpoint still returns a plain array.
export const fetchReceivingInvestigationFilePaginated = async (
  params?: ListParams,
) => {
  const qs = buildListQuery(params);
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/${qs ? `?${qs}` : ""}`,
  );
  return toPage<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByCoordinatorPaginated = async (
  admin_unit: string | number,
  org_id: string | number,
  params?: ListParams,
) => {
  const qs = buildListQuery(params, {
    admin_unit_id: admin_unit,
    org_id,
  });
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-admin-org/?${qs}`,
  );
  return toPage<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByCoordinatorWithTypePaginated =
  async (
    admin_unit: string | number,
    role: string,
    params?: ListParams,
  ) => {
    const qs = buildListQuery(params, {
      admin_unit_id: admin_unit,
      coordinator_role: role,
    });
    const response = await apiClient.get(
      `/prosecutor-police/investigation-files/by-admin-org-coordinator/?${qs}`,
    );
    return toPage<InvestigationFile>(response.data);
  };
export const fetchReceivingInvestigationFileByProsecutorPaginated = async (
  assign_prosecutor_id: string | number,
  params?: ListParams,
) => {
  const qs = buildListQuery(params, {
    assign_prosecutor_id,
  });
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-prosecutor-id/?${qs}`,
  );
  return toPage<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileByPolicePaginated = async (
  police_station_id: string | number,
  params?: ListParams,
) => {
  const qs = buildListQuery(params, {
    police_station_id,
  });
  const response = await apiClient.get(
    `/prosecutor-police/investigation-files/by-police-station/?${qs}`,
  );
  return toPage<InvestigationFile>(response.data);
};
export const fetchReceivingInvestigationFileById = async (uu_id: number) => {
  const response = await apiClient.get<InvestigationFile>(
    "/prosecutor-police/investigation-files/" + uu_id + "/",
  );
  return response.data;
};
export const addReceivingInvestigationFile = async (
  data: InvestigationFile,
) => {
  const response = await apiClient.post(
    "/prosecutor-police/investigation-files/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editReceivingInvestigationFile = async (
  data: InvestigationFile,
) => {
  const response = await apiClient.patch(
    "/prosecutor-police/investigation-files/" + data?.id + "/",

    data,
  );
  return response.data;
};
export const editReceivingInvestigationFile2 = async (data: any) => {
  const response = await apiClient.patch(
    "/prosecutor-police/investigation-files/" + data?.id + "/",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
export const editReceivingInvestigationFile3 = async (data: any) => {
  const response = await apiClient.patch(
    "/prosecutor-police/investigation-files/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteReceivingInvestigationFile = async (
  reporter: InvestigationFile,
) => {
  const response = await apiClient.delete(
    "/prosecutor-police/investigation-files/" + reporter?.id + "/",
  );
  return response.data;
};

// ProsecutorFeedback
export const fetchProsecutorFeedback = async () => {
  const response = await apiClient.get<ProsecutorDecisionFeedback[]>(
    "/prosecutor-police/prosecutor-feedbacks/",
  );
  return response.data;
};
export const addProsecutorFeedback = async (
  data: ProsecutorDecisionFeedback,
) => {
  const response = await apiClient.post(
    "/prosecutor-police/prosecutor-feedbacks/",
    data,
  );
  return response.data;
};
export const editProsecutorFeedback = async (
  data: ProsecutorDecisionFeedback,
) => {
  const response = await apiClient.put(
    "/prosecutor-police/prosecutor-feedbacks/" + data?.uu_id + "/",
    data,
  );
  return response.data;
};
export const deleteProsecutorFeedback = async (
  data: ProsecutorDecisionFeedback,
) => {
  const response = await apiClient.delete(
    "/prosecutor-police/prosecutor-feedbacks/" + data?.uu_id + "/",
  );
  return response.data;
};

// FurtherInvestigation
export const fetchFurtherInvestigation = async () => {
  const response = await apiClient.get<FurtherInvestigation[]>(
    "/prosecutor-police/further-investigation-requests/",
  );
  return response.data;
};
export const fetchFurtherInvestigationByAdminUnitAndRole = async (
  admin_unit: string,
  role: string,
) => {
  const response = await apiClient.get<FurtherInvestigation[]>(
    `/prosecutor-police/further-investigation-requests/by-admin-org-coordinator/?admin_unit_id=${admin_unit}&coordinator_role=${role}`,
  );
  return response.data;
};
export const fetchFurtherInvestigationById = async (id: number) => {
  const response = await apiClient.get<FurtherInvestigation>(
    "/prosecutor-police/further-investigation-requests/" + id + "/",
  );
  return response.data;
};
export const fetchFurtherInvestigationByCRNumberAndIncidentRegNo = async (
  crime_reference_number: string,
  incident_registration_number: string,
) => {
  const response = await apiClient.get<FurtherInvestigation[]>(
    `/prosecutor-police/further-investigation-requests/?crime_registration_number=${crime_reference_number}&incident_registration_number=${incident_registration_number}`,
  );
  return response.data;
};
// Build a real FormData for further-investigation requests so files upload
// correctly. `suspects_ids` is repeated per id; nested objects/collections are
// skipped. Content-Type is intentionally NOT set — the browser adds the
// multipart boundary for a FormData body.
const buildFurtherInvestigationFormData = (data: any): FormData => {
  const fd = new FormData();
  const skip = ["suspects", "feedbacks", "documents", "uu_id", "id"];
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (skip.includes(key)) return;

    if (key === "suspects_ids" && Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null)
          fd.append("suspects_ids", String((v as any)?.uu_id ?? v));
      });
      return;
    }
    if (value instanceof File) {
      fd.append(key, value);
      return;
    }
    if (value instanceof Date) {
      fd.append(key, value.toISOString());
      return;
    }
    if (typeof value === "object") {
      const id = (value as any)?.id ?? (value as any)?.uu_id;
      if (id !== undefined && id !== null) fd.append(key, String(id));
      return;
    }
    if (typeof value === "boolean") {
      fd.append(key, value ? "true" : "false");
      return;
    }
    fd.append(key, String(value));
  });
  return fd;
};

export const addFurtherInvestigation = async (data: FurtherInvestigation) => {
  const response = await apiClient.post(
    "/prosecutor-police/further-investigation-requests/",
    buildFurtherInvestigationFormData(data),
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};
export const editFurtherInvestigation = async (data: FurtherInvestigation) => {
  const response = await apiClient.patch(
    "/prosecutor-police/further-investigation-requests/" + data?.id + "/",
    buildFurtherInvestigationFormData(data),
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};
export const editFurtherInvestigation2 = async (data: any) => {
  const response = await apiClient.patch(
    "/prosecutor-police/further-investigation-requests/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteFurtherInvestigation = async (
  reporter: FurtherInvestigation,
) => {
  const response = await apiClient.delete(
    "/prosecutor-police/further-investigation-requests/" +
      reporter?.uu_id +
      "/",
  );
  return response.data;
};

// FurtherInvestigation
export const fetchFurtherInvestigationFeedback = async () => {
  const response = await apiClient.get<FurtherInvestigationFeedback[]>(
    "/prosecutor-police/further-investigation-feedbacks/",
  );
  return response.data;
};
export const fetchFurtherInvestigationFeedbackById = async (id: number) => {
  const response = await apiClient.get<FurtherInvestigationFeedback>(
    "/prosecutor-police/further-investigation-feedbacks/" + id + "/",
  );
  return response.data;
};
export const addFurtherInvestigationFeedback = async (
  data: FurtherInvestigationFeedback,
) => {
  const response = await apiClient.post(
    "/prosecutor-police/further-investigation-feedbacks/",
    data,
  );
  return response.data;
};
export const editFurtherInvestigationFeedback = async (
  data: FurtherInvestigationFeedback,
) => {
  const response = await apiClient.patch(
    "/prosecutor-police/further-investigation-feedbacks/" + data?.id + "/",
    data,
  );
  return response.data;
};
export const deleteFurtherInvestigationFeedback = async (
  reporter: FurtherInvestigationFeedback,
) => {
  const response = await apiClient.delete(
    "/prosecutor-police/further-investigation-feedbacks/" + reporter?.id + "/",
  );
  return response.data;
};
export const fetchPreliminaryByWitness = async (witness: string) => {
  const response = await apiClient.get<PreliminaryInquiry[]>(
    `/crime-reference/preliminary-inquiries/?witness_id==${witness}`,
  );
  return response.data;
};

// COORDINATOR_COMMENT
export const fetchCoordinatorComment = async () => {
  const response = await apiClient.get<CoordinatorCommentOnInvestigationFile[]>(
    "/prosecutor-police/cordinator-comments/",
  );
  return response.data;
};
export const addCoordinatorComment = async (
  comment: CoordinatorCommentOnInvestigationFile,
) => {
  const response = await apiClient.post(
    "/prosecutor-police/cordinator-comments/",
    comment,
  );
  return response.data;
};
export const editCoordinatorComment = async (
  comment: CoordinatorCommentOnInvestigationFile,
) => {
  const response = await apiClient.put(
    "/prosecutor-police/cordinator-comments/" + comment?.id + "/",
    comment,
  );
  return response.data;
};
export const deleteCoordinatorComment = async (
  comment: CoordinatorCommentOnInvestigationFile,
) => {
  const response = await apiClient.delete(
    "/prosecutor-police/coordinator-comments/" + comment?.id + "/",
  );
  return response.data;
};
